import { Serializable } from '@/models/core/serializable';
import { GEnum } from '@/constants/enum.defination';
import { GradeSchoolClass, SchoolClass } from '@/models/school-class/school-class';
import { Student } from '@/models/student/student';
import { EvalSettingTitleLang } from '@/models/evalSetting/eval-setting-title-lang';
import { EvalSettingMail } from '@/models/evalSetting/eval-setting-mail';
import { QuestionType } from '@/models/evalSetting/question-type';

export class EvalSetting implements Serializable<EvalSetting> {
  evalSettingID: number; // 受検ID
  title: string = ''; // 受検コース名
  titleLangs: EvalSettingTitleLang[] = []; // 受検コース名（多言語用）
  startTime: Date = new Date(); // 開始日
  deadline: Date = new Date(); // 締切日
  questionSettingType: number = -1; // 設問タイプ
  competencySettingType: GEnum.CompetencySettingType = GEnum.CompetencySettingType.Full; // 設問数選択
  competencyIDs: number[] = []; // 受検コースに設定したコンピテンシーID一覧
  trendIDs: number[] = []; // 受検コースに設定した傾向ID一覧
  iatRequired: boolean = true; // 気質診断必須有無
  iatInactiveFlg: boolean = false; // 気質診断非活性フラグ
  selfEvalRequired: boolean = true; // 自己評価必須有無
  giftRequired: boolean = true; // 他者評価必須有無
  freeWordSkillRequired: boolean = true; // 強み評価
  trendRequired: boolean = false; // 傾向チェック必須有無
  candidateWebLink: string = ''; // 評価者リンク
  isEvaluatorVisible: boolean; // 受検画面への評価者表示有無
  isProgressNotification: boolean = false; // 通知有無
  requiredRespondents: number = 3; // 最低評価者人数
  schoolClasses: SchoolClass[] = []; // 受検対象者クラス一覧
  students: Student[] = []; // 受検対象者生徒一覧
  gradeSchoolClasses: GradeSchoolClass[]; // 学年別クラス一覧
  status: number = 0; // ステータス
  isPostingReportEnabled: boolean = true; // 受検画面へのフィードバックレポート表示有無
  // 受検者による他者評価リクエスト許可区分
  evaluationRequestPermissionCategory: GEnum.EvaluationRequestPermissionCategory = GEnum.EvaluationRequestPermissionCategory.NotPermit;
  // レポート表示種別
  personalReportDisplayType: GEnum.PersonalReportDisplayType = GEnum.PersonalReportDisplayType.SelfGrowthSupport;
  selectedReportLink: GEnum.PersonalReportDisplayType = GEnum.PersonalReportDisplayType.NoShow;
  noAssignedExamineeCount: number = 0; // 未割り当て生徒数(=受検設定後にクラス変更をした生徒の数)
  isEvaluatorVisibleRadio: GEnum.RadioLabel = GEnum.RadioLabel.False;
  isProgressNotificationRadio: GEnum.RadioLabel = GEnum.RadioLabel.False;
  freeWordSkillRequiredRadio: GEnum.RadioLabel = GEnum.RadioLabel.False;
  isPostingReportEnabledRadio: GEnum.RadioLabel = GEnum.RadioLabel.False;
  trendRequiredRadio: GEnum.RadioLabel = GEnum.RadioLabel.False;
  mailSetting: EvalSettingMail = new EvalSettingMail();
  hasEvalRequest: boolean = false;
  hasEvalRequestApproval: boolean = false;

  deserialize(input: any): EvalSetting {
    this.candidateWebLink = input.candidateWebLink;
    this.competencyIDs = input.competencyIDs || [];
    this.competencySettingType = input.competencySettingType;
    this.evalSettingID = input.evalSettingID;
    this.freeWordSkillRequired = input.freeWordSkillRequired;
    this.freeWordSkillRequiredRadio = input.freeWordSkillRequired ? GEnum.RadioLabel.True : GEnum.RadioLabel.False;
    this.trendIDs = input.trendIDs || [];
    this.giftRequired = input.giftRequired;
    this.iatRequired = input.iatRequired;
    this.trendRequired = input.trendRequired;
    this.trendRequiredRadio = input.trendRequired ? GEnum.RadioLabel.True : GEnum.RadioLabel.False;
    this.isProgressNotification = input.isProgressNotification;
    this.isProgressNotificationRadio = input.isProgressNotification ? GEnum.RadioLabel.True : GEnum.RadioLabel.False;
    this.questionSettingType = input.questionSettingType;
    this.requiredRespondents = input.requiredRespondents;
    this.schoolClasses = input.schoolClasses;
    this.students = input.students === null || input.students === undefined ? [] : input.students.map((student: Student) => new Student().deserialize(student));
    if (input.schoolClasses != undefined) {
      this.gradeSchoolClasses = this.convertGradeSchoolClasses(input.schoolClasses);
    }
    this.isEvaluatorVisible = input.isEvaluatorVisible;
    this.isEvaluatorVisibleRadio = this.isEvaluatorVisible ? GEnum.RadioLabel.True : GEnum.RadioLabel.False;
    this.isPostingReportEnabled = input.isPostingReportEnabled;
    this.isPostingReportEnabledRadio = this.isPostingReportEnabled ? GEnum.RadioLabel.True : GEnum.RadioLabel.False;
    this.evaluationRequestPermissionCategory = input.evaluationRequestPermissionCategory;
    this.personalReportDisplayType = input.personalReportDisplayType;
    this.selectedReportLink = this.getSelectedReportLink(this.personalReportDisplayType, this.isPostingReportEnabled);
    this.selfEvalRequired = input.selfEvalRequired;
    this.startTime = new Date(input.startTime);
    this.deadline = new Date(input.deadline);
    this.status = input.status;
    this.title = input.title;
    this.titleLangs = input.titleLangs === null || input.titleLangs === undefined ? [] : input.titleLangs.map((titleLang: EvalSettingTitleLang) => new EvalSettingTitleLang().deserialize(titleLang));
    this.noAssignedExamineeCount = input.noAssignedExamineeCount;
    if (input.mailSetting != undefined) {
      this.mailSetting =  new EvalSettingMail().deserialize(input.mailSetting);
    }
    this.hasEvalRequest = input.hasEvalRequest;
    this.hasEvalRequestApproval= input.hasEvalRequestApproval;

    return this;
  }

  // クラス別、または、学年別の生徒人数(key:クラスID or 学年,value:人数)
  studentCountByDomain(domainType: GEnum.TargetDomainType) {
    return this.students.reduce((result: Map<number, number>, student: Student) => {
      let key: number = 0;
      switch (domainType) {
        case GEnum.TargetDomainType.SchoolClass:
          key = student.schoolClass.schoolClassID;
          break;
        case GEnum.TargetDomainType.Grade:
          key = student.schoolClass.grade;
          break;
      }
      if (!result.has(key)) {
        result.set(key, 0);
      }
      const count: number = result.get(key) + 1;
      result.set(key, count);
      return result;
    }, new Map<number, number>());
  }

  get canRandomSchoolClass(): boolean {
    const studentCountBySchoolClass: Map<number, number> = this.studentCountByDomain(GEnum.TargetDomainType.SchoolClass);
    if (studentCountBySchoolClass.size === 0) {
      return false;
    }
    return this.students.every((student: Student) => studentCountBySchoolClass.get(student.schoolClass.schoolClassID) > this.requiredRespondents);
  }

  get canRandomGrade(): boolean {
    const studentCountBySchoolClass: Map<number, number> = this.studentCountByDomain(GEnum.TargetDomainType.Grade);
    if (studentCountBySchoolClass.size === 0) {
      return false;
    }
    return this.students.every((student: Student) => studentCountBySchoolClass.get(student.schoolClass.grade) > this.requiredRespondents);
  }

  get canRandomAll(): boolean {
    return this.students.length > this.requiredRespondents;
  }

  // モデル初期化
  clean() {
    this.title = '';
    this.startTime = null;
    this.deadline = null;
    this.iatRequired = true;
    this.selfEvalRequired = true;
    this.giftRequired = true;
    this.trendRequired = false;
    this.requiredRespondents = 3;
    this.isProgressNotification = false;
    this.competencySettingType = GEnum.CompetencySettingType.Full;
    this.isEvaluatorVisibleRadio = GEnum.RadioLabel.False;
    this.isPostingReportEnabledRadio = GEnum.RadioLabel.False;
    this.isProgressNotificationRadio = GEnum.RadioLabel.False;
    this.freeWordSkillRequiredRadio = GEnum.RadioLabel.False;
    this.evaluationRequestPermissionCategory = GEnum.EvaluationRequestPermissionCategory.NotPermit;
    this.personalReportDisplayType = GEnum.PersonalReportDisplayType.SelfGrowthSupport;
    this.trendRequiredRadio = GEnum.RadioLabel.False;
    this.status = 0;
    this.students = [];
    this.schoolClasses = [];
    this.noAssignedExamineeCount = 0;
    this.trendIDs = [];
    this.titleLangs = [];
    this.mailSetting = new EvalSettingMail();
  }

  // 傾向チェックに関するものだけを初期化
  cleanTrend() {
    this.trendRequired = false;
    this.trendRequiredRadio = GEnum.RadioLabel.False;
    this.trendIDs = [];
  }

  // メール設定に関するものだけを初期化
  cleanMailSetting() {
    this.mailSetting = new EvalSettingMail();
  }

  convertGradeSchoolClasses(inputSchoolClasses: any[]): GradeSchoolClass[] {
    let schoolClassByGrade: any = new Map();

    inputSchoolClasses.forEach((value: any) => {
      if (!schoolClassByGrade.has(value.grade)) {
        schoolClassByGrade.set(value.grade, []);
      }
      schoolClassByGrade.get(value.grade).push(value);
    });

    const grades = [...schoolClassByGrade.keys()].sort();
    const result: GradeSchoolClass[] = grades.map((grade: number) => {
      return new GradeSchoolClass().deserialize({ grade, schoolClasses: schoolClassByGrade.get(grade) });
    });

    return result;
  }

  get iatRequiredOnly(): boolean {
    return this.iatRequired && !this.selfEvalRequired && !this.giftRequired;
  }

  get trendRequiredOnly(): boolean {
    return !this.iatRequired && !this.selfEvalRequired && !this.giftRequired && this.trendRequired;
  }

  get isBeforeStart(): boolean {
    return this.status === GEnum.ExamStatus.BeforeStart;
  }

  get isUnderExamination(): boolean {
    return this.status === GEnum.ExamStatus.UnderExamination;
  }

  get isBeforeEnd(): boolean {
    return this.status === GEnum.ExamStatus.BeforeStart || this.status === GEnum.ExamStatus.UnderExamination;
  }

  /**
   * reportEnabled(レポート表示可否)が true(表示する)場合は reportDisplayType を返却する。
   * false(表示しない)の場合は GEnum.PersonalReportDisplayType.NoShow を返却する。
   * @param reportDisplayType
   * @param reportEnabled
   * @returns
   */
  getSelectedReportLink(reportDisplayType: number, reportEnabled: boolean): GEnum.PersonalReportDisplayType {
    return reportEnabled ? reportDisplayType : GEnum.PersonalReportDisplayType.NoShow;
  }

  /**
   * refreshQuestionSettingType
   * 現在設定されている設問設定(questionSettingType)が、現在は使用されていないもの（削除された等）の場合、
   * 使用可能な設問設定の中で、IDが一番小さい（若い）ものを設定する
   */
  refreshQuestionSettingType(questionTypes: QuestionType[]) {
    // 選択可能な設問設定の中に、現在設定されている設問設定（ID）と一致するものが存在するか
    const found = questionTypes.find((questionType: QuestionType) => this.questionSettingType === questionType.questionTypeID);
    // 存在しない場合、現在設定されている設問設定を更新する
    if (!found) {
      this.questionSettingType = questionTypes[0].questionTypeID;
    }
  }
}

export interface EvalSettingDetailRequest {
  title: string;
  titleLangs: EvalSettingTitleLang[];
  startTime: string;
  deadline: string;
  questionSettingType: number;
  competencySettingType?: number;
  competencyIDs?: number[];
  iatRequired?: boolean;
  selfEvalRequired?: boolean;
  giftRequired?: boolean;
  freeWordSkillRequired?: boolean;
  trendRequired?: boolean;
  trendIDs?: number[];
  schoolClassIDs?: number[];
  studentIDs?: number[];
  isEvaluatorVisible?: boolean;
  requiredRespondents?: number;
  evaluationRequestPermissionCategory: number;
  personalReportDisplayType: number;
  isProgressNotification?: boolean;
  mailSetting: EvalSettingMail;
}

export class ImportRequestResults implements Serializable<ImportRequestResults> {
  registrationCount: number;

  deserialize(input: any): ImportRequestResults {
    this.registrationCount = input.registrationCount;
    return this;
  }
}

export class UpdateExamineeStudentsResult implements Serializable<UpdateExamineeStudentsResult> {
  updatedCount: number;

  deserialize(input: any): UpdateExamineeStudentsResult {
    this.updatedCount = input.updatedCount;
    return this;
  }
}

export class GrowCourseScoreUpdateStatus {
  id: number;
  giftCourseId: number;
  competencyCount: number;
  deadline: Date;
  updateRequired: boolean;
  state: boolean;
  isFixed: boolean;
  isForceReport: boolean;
  updatedAt: Date;

  deserialize(input: any) {
    this.id = input.id;
    this.giftCourseId = input.giftCourseId;
    this.competencyCount = input.competencyCount;
    this.deadline = input.deadline;
    this.updateRequired = input.updateRequired;
    this.isFixed = input.isFixed;
    this.isForceReport = input.isForceReport;
    this.state = input.state;
    this.updatedAt = input.updatedAt;
    return this;
  }
}
